import { useMemo } from "react";
import {
  groupByCompany, buildIncidents, countStatuses, health, deviceStatus, serverStatus,
  KIND_LABEL, KIND_ICON,
} from "./dashboardHelpers.js";
import { CardGrid, Section, KpiTile, StatusBadge } from "./DashboardParts.jsx";
import ClientCard from "./ClientCard.jsx";

// ══════════════════════════════════════════════════════
//  Onglet Vue d'ensemble — bandeau de synthèse, tous les clients,
//  puis la liste détaillée des alertes et incidents actifs.
// ══════════════════════════════════════════════════════

export default function OverviewTab({ data, onOpenDevice }) {
  const { nas, gateways, servers, loading, errors } = data;
  const groups    = useMemo(() => groupByCompany({ nas, gateways, servers }), [nas, gateways, servers]);
  const incidents = useMemo(() => buildIncidents({ nas, gateways, servers }), [nas, gateways, servers]);
  const error = [errors.monitoring, errors.servers].filter(Boolean).join(" · ");

  const c = countStatuses([...gateways.map(deviceStatus), ...nas.map(deviceStatus), ...servers.map(serverStatus)]);
  const total = c.OK + c.WARNING + c.CRITICAL;
  const level = c.CRITICAL ? "CRITICAL" : c.WARNING ? "WARNING" : "OK";
  const bannerText = c.CRITICAL
    ? `${c.CRITICAL} appareil(s) en état critique — intervention requise`
    : c.WARNING ? `${c.WARNING} appareil(s) en avertissement — à surveiller`
    : "Tous les appareils supervisés fonctionnent normalement";

  return (
    <div>
      {!loading && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, borderRadius: 12, padding: "12px 16px", marginBottom: 14,
          fontWeight: 600, fontSize: 14, color: health(level).color, background: health(level).bg, border: "1px solid var(--border)",
        }}>
          <i className={`bi ${level === "OK" ? "bi-check-circle" : "bi-exclamation-triangle"}`} style={{ fontSize: 18 }} />
          {bannerText}
        </div>
      )}

      <div className="dash-metrics-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 12, marginBottom: 18 }}>
        <KpiTile label="Appareils supervisés" value={total} sub={`${groups.length} client(s)`} />
        <KpiTile label="OK" value={c.OK} color={health("OK").color} />
        <KpiTile label="Avertissements" value={c.WARNING} color={c.WARNING ? health("WARNING").color : "var(--text-3)"} />
        <KpiTile label="Critiques" value={c.CRITICAL} color={c.CRITICAL ? health("CRITICAL").color : "var(--text-3)"} />
      </div>

      <div className="itm-card" style={{ marginBottom: 18 }}>
        <div className="card-title-row">
          <div>
            <div className="card-title">Tous les sites</div>
            <div className="card-sub">Serveurs, NAS et UDM regroupés par client — du plus urgent au plus calme</div>
          </div>
        </div>
        <Section loading={loading} error={error} count={groups.length} empty="Aucun appareil supervisé pour le moment.">
          <CardGrid min={320}>{groups.map(g => <ClientCard key={g.company} group={g} />)}</CardGrid>
        </Section>
      </div>

      <div className="itm-card">
        <div className="card-title-row">
          <div>
            <div className="card-title">Alertes & incidents actifs</div>
            <div className="card-sub">Calculés en direct à partir des dernières synchronisations — du plus critique au plus léger</div>
          </div>
        </div>
        {loading ? <Section loading /> : incidents.length === 0 ? (
          <div style={{ textAlign: "center", padding: 28, color: "var(--text-2)", fontSize: 13 }}>
            <i className="bi bi-check-circle" style={{ fontSize: 28, color: "var(--success)", display: "block", marginBottom: 6 }} />
            Aucun incident actif.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {incidents.map(i => (
              <div key={i.key} style={{
                border: "1px solid var(--border)", borderLeft: `4px solid ${health(i.level).color}`, borderRadius: 10,
                padding: "10px 14px", display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap",
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <i className={`bi ${KIND_ICON[i.kind]}`} style={{ color: "var(--text-3)" }} />
                    <strong style={{ fontSize: 13.5, color: "var(--text-1)" }}>{i.device}</strong>
                    <span style={{ fontSize: 12, color: "var(--text-3)" }}>{i.company} · {KIND_LABEL[i.kind]}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-1)", marginTop: 3 }}>{i.title}</div>
                  {i.detail && <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2, wordBreak: "break-word" }}>{i.detail}</div>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <StatusBadge status={i.level} />
                  {i.kind !== "server" && onOpenDevice && (
                    <button className="btn-itm btn-itm-outline" style={{ padding: "3px 10px", fontSize: 11 }}
                      onClick={() => onOpenDevice(i.kind, i.id)}>Fiche →</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}